import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Dialog } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';

import { useEffect, useState } from 'react';
import { useFetcher, useLocation, useNavigate } from '@remix-run/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler, FieldValues } from 'react-hook-form';
import {
    API_ACTIONS,
    DESIGN_BOARD_REMIX_API,
} from '@modules/design-board/config';
import { DesignBoardDrawerHeader } from './DesignBoardRIghtSideDrawerHeader';
import { DesignBoardDrawerFooter } from './DesignBoardRightSideDrawerFooter';
import NewDesignBoardDrawerContent from './NewDesignBoardBody';
import { useDesignBoardPageState } from '../context/DesignBoardContext';
import * as S from './RightSideNewBoarderDrawer.style';
import { DeleteDesignBoardDialog } from './DeleteDesignBoardDialog';
import type { NewBoardFormShape } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { NewBoardValidator } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { AllowedActions } from '@modules/design-board/actions/handler-mappings';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useSaveToDesignBoardContext } from '~/modules/design-board/component/context';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useFetcherIsActionRedirect } from '~/hooks/useFetcherIsActionRedirect';
import type { DesignBoardActionResponse } from '~/routes/remix-api.design-board.design-board';

const DeleteDesignBoardConfirmation = (props: {
    open: boolean;
    onCancel: () => void;
    onDelete: () => void;
    loading: boolean;
}) => {
    const { css } = useStyles();
    return (
        <Dialog
            open={props.open}
            onCancel={props.onCancel}
            content={
                <DeleteDesignBoardDialog
                    onDelete={props.onDelete}
                    onCancel={props.onCancel}
                    loading={props.loading}
                />
            }
            className={css(S.DeleteDialog)}
            footer={null}
            header={null}
        />
    );
};

const DesignBoardDrawerEditMainContent = () => {
    const { dispatch, state } = useDesignBoardPageState();
    const location = useLocation();
    const navigate = useNavigate();
    const { toast } = useToaster();
    const fetcher = useFetcher<DesignBoardActionResponse>();
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const [isEditBoardLoading, setIsEditBoardLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const saveToBoardContext = useSaveToDesignBoardContext();

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<NewBoardFormShape>({
        resolver: zodResolver(NewBoardValidator),
        mode: 'onBlur',
        ...(state.designBoardInput && {
            defaultValues: state.designBoardInput,
        }),
    });

    const isEditing = state.designBoardInput !== undefined;
    const isDeletingDesignBoard =
        (fetcher.state === 'loading' || fetcher.state === 'submitting') &&
        !!fetcher?.formData?.has('_action') &&
        fetcher.formData?.get('_action') ===
            AllowedActions.enum.delete_design_board;

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        const { title, isPrivate, description = '' } = data;
        setIsEditBoardLoading(true);

        const pinAction = state.CreateBoardFromPinActionType;
        const pinData =
            pinAction === 'NoPins' || !state.pinMoveData
                ? undefined
                : {
                      pinIds: state.pinMoveData.pinListString,
                      sourceBoardId: `${state.pinMoveData.boardId}`,
                  };

        if (title && !errors?.title?.message) {
            fetcher.submit(
                {
                    ...data,
                    description,
                    id:
                        state.designBoardInput === undefined
                            ? '0'
                            : `${state.designBoardInput.id}`,
                    isPrivate: isPrivate ? '1' : '0',
                    ...pinData,
                    _action: isEditing
                        ? API_ACTIONS.UPDATE_DESIGN_BOARD
                        : pinAction === 'MovePins'
                        ? API_ACTIONS.CREATE_DESIGN_BOARD_AND_MOVE_ITEM
                        : pinAction === 'CopyPins'
                        ? API_ACTIONS.CREATE_DESIGN_BOARD_AND_COPY_ITEM
                        : API_ACTIONS.CREATE_DESIGN_BOARD_AND_SAVE_ITEM,
                },
                {
                    method: 'post',
                    action: DESIGN_BOARD_REMIX_API,
                }
            );
        }
    };

    const onDelete = () => {
        if (state.designBoardInput !== undefined) {
            fetcher.submit(
                {
                    boardId: `${state.designBoardInput.id}`,
                    _action: AllowedActions.enum.delete_design_board,
                },
                {
                    method: 'post',
                    action: location.pathname,
                }
            );

            //This is a hack to show a spinner while the delete request is being processed, otherwise it won't match design.
            const timeout = setTimeout(() => {
                setIsDialogOpen(false);
                clearTimeout(timeout);
                dispatch({
                    type: 'CloseDrawer',
                });
                reset();
            }, 350);
        }
    };

    const onClose = (_openValue: boolean = false) => {
        dispatch({
            type: 'CloseDrawer',
        });
        reset();
    };
    const isActionRedirect = useFetcherIsActionRedirect(fetcher);
    useEffect(() => {
        if (
            (fetcherIsDone(fetcher) || isActionRedirect) &&
            saveToBoardContext.state.myBoardList.length > 0
        ) {
            saveToBoardContext.dispatch({
                type: 'SetBoardPinData',
                payload: {
                    myBoardList: [],
                },
            });
        }
    }, [fetcher, saveToBoardContext, isActionRedirect]);

    useEffect(() => {
        if (
            isEditBoardLoading &&
            (fetcherIsDone(fetcher) || isActionRedirect)
        ) {
            if (fetcher.data === undefined) {
                toast('Design board has been updated.', { type: 'success' });
                dispatch({
                    type: 'CloseDrawer',
                });
                reset();
            } else if ('boardId' in fetcher.data) {
                const toastMessage = isEditing
                    ? `Design board "${fetcher.data.title}" has been updated.`
                    : `
                        New design board "${fetcher.data.title}" has been saved.
                        ${fetcher.data.toastMessageExtra || ''}
                    `;
                toast(toastMessage, { type: 'success' });

                dispatch({
                    type: 'CloseDrawer',
                });
                reset();
            } else {
                toast(fetcher.data.message, { type: 'error' });
            }
            setIsEditBoardLoading(false);
        }
    }, [
        isEditBoardLoading,
        fetcher,
        reset,
        isEditing,
        dispatch,
        navigate,
        state,
        toast,
        isActionRedirect,
    ]);

    const modalClose = () => {
        setIsDialogOpen(false);
    };

    return (
        <fetcher.Form
            className={css(S.FormClass)}
            onSubmit={handleSubmit(onSubmit)}
        >
            <DesignBoardDrawerHeader
                {...{ onClose }}
                headerTitle={
                    !isEditing
                        ? mobile
                            ? 'Create board'
                            : 'Create a board'
                        : 'Edit board'
                }
            />

            <NewDesignBoardDrawerContent control={control} errors={errors} />

            <DesignBoardDrawerFooter
                loading={isEditBoardLoading}
                {...(state.designBoardInput
                    ? {
                          ...{
                              submitButtonLabel: 'SAVE',
                              buttonSecondary: {
                                  onClick: () => setIsDialogOpen(true),
                                  label: 'DELETE BOARD',
                              },
                          },
                      }
                    : {
                          ...{
                              submitButtonLabel: 'CREATE',
                          },
                      })}
            />
            <DeleteDesignBoardConfirmation
                open={isDialogOpen}
                onCancel={() => modalClose()}
                onDelete={() => onDelete()}
                loading={isDeletingDesignBoard}
            />
        </fetcher.Form>
    );
};

export default DesignBoardDrawerEditMainContent;
